<template>
  <v-container fluid>
    <v-card class="mx-auto" max-width="1200">
      <v-card-title class="warning white--text">
        <v-icon left color="white">mdi-calendar-remove</v-icon>
        Time Away Management
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Add Time Away Form -->
        <v-card outlined class="mb-6">
          <v-card-subtitle class="primary--text">
            <v-icon left color="primary">mdi-plus-circle</v-icon>
            <strong>Add New Time Away</strong>
          </v-card-subtitle>

          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="newTimeAway.type"
                  :items="timeAwayTypes"
                  label="Time Away Type"
                  outlined
                  dense
                  prepend-inner-icon="mdi-calendar-clock"
                  @change="onTypeChange"
                ></v-select>
              </v-col>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="newTimeAway.reason"
                  label="Reason (Optional)"
                  outlined
                  dense
                  prepend-inner-icon="mdi-text"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Hours Away -->
            <v-row v-if="newTimeAway.type === 'hours'">
              <v-col cols="12" md="4">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="newTimeAway.date"
                      label="Date"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      :rules="[(v) => !!v || 'Date is required']"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="newTimeAway.date"
                    @input="dateMenu = false"
                    :min="minDate"
                    :show-current="true"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="newTimeAway.startTime"
                  :items="availableStartTimeOptions"
                  item-text="label"
                  item-value="value"
                  label="Start Time"
                  outlined
                  dense
                  prepend-inner-icon="mdi-clock-start"
                  :rules="[(v) => !!v || 'Start time is required']"
                  :hint="
                    isSelectedDateToday
                      ? 'Times before current time are disabled'
                      : ''
                  "
                  persistent-hint
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="newTimeAway.endTime"
                  :items="availableEndTimeOptions"
                  item-text="label"
                  item-value="value"
                  label="End Time"
                  outlined
                  dense
                  prepend-inner-icon="mdi-clock-end"
                  :rules="[(v) => !!v || 'End time is required']"
                  :hint="
                    newTimeAway.startTime ? 'Must be after start time' : ''
                  "
                  persistent-hint
                ></v-select>
              </v-col>
            </v-row>

            <!-- Day Away -->
            <v-row v-if="newTimeAway.type === 'day'">
              <v-col cols="12" md="4">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="newTimeAway.date"
                      label="Date"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      :rules="[(v) => !!v || 'Date is required']"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="newTimeAway.date"
                    @input="dateMenu = false"
                    :min="minDate"
                    :show-current="true"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="8">
                <v-switch
                  v-model="newTimeAway.allDay"
                  label="All Day"
                  color="primary"
                  hide-details
                  class="mt-2"
                ></v-switch>
              </v-col>
            </v-row>

            <!-- Day Away Time Selection (when not all day) -->
            <v-row v-if="newTimeAway.type === 'day' && !newTimeAway.allDay">
              <v-col cols="12" md="6">
                <v-select
                  v-model="newTimeAway.startTime"
                  :items="availableStartTimeOptions"
                  item-text="label"
                  item-value="value"
                  label="Start Time"
                  outlined
                  dense
                  prepend-inner-icon="mdi-clock-start"
                  :rules="[(v) => !!v || 'Start time is required']"
                  :hint="
                    isSelectedDateToday
                      ? 'Times before current time are disabled'
                      : ''
                  "
                  persistent-hint
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="newTimeAway.endTime"
                  :items="availableEndTimeOptions"
                  item-text="label"
                  item-value="value"
                  label="End Time"
                  outlined
                  dense
                  prepend-inner-icon="mdi-clock-end"
                  :rules="[(v) => !!v || 'End time is required']"
                  :hint="
                    newTimeAway.startTime ? 'Must be after start time' : ''
                  "
                  persistent-hint
                ></v-select>
              </v-col>
            </v-row>

            <!-- Days Away (Range) -->
            <v-row v-if="newTimeAway.type === 'days'">
              <v-col cols="12" md="4">
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="newTimeAway.startDate"
                      label="Start Date"
                      prepend-inner-icon="mdi-calendar-start"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      :rules="[(v) => !!v || 'Start date is required']"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="newTimeAway.startDate"
                    @input="startDateMenu = false"
                    :min="minDate"
                    :show-current="true"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4">
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="newTimeAway.endDate"
                      label="End Date"
                      prepend-inner-icon="mdi-calendar-end"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      :rules="[(v) => !!v || 'End date is required']"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="newTimeAway.endDate"
                    @input="endDateMenu = false"
                    :min="minEndDate"
                    :show-current="true"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="newTimeAway.allDay"
                  label="All Day"
                  color="primary"
                  hide-details
                  class="mt-2"
                ></v-switch>
              </v-col>
            </v-row>

            <!-- Days Away Time Selection (when not all day) -->
            <v-row v-if="newTimeAway.type === 'days' && !newTimeAway.allDay">
              <v-col cols="12" md="6">
                <v-select
                  v-model="newTimeAway.startTime"
                  :items="availableStartTimeOptions"
                  item-text="label"
                  item-value="value"
                  label="Start Time"
                  outlined
                  dense
                  prepend-inner-icon="mdi-clock-start"
                  :rules="[(v) => !!v || 'Start time is required']"
                  :hint="
                    isStartDateToday
                      ? 'Times before current time are disabled'
                      : ''
                  "
                  persistent-hint
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="newTimeAway.endTime"
                  :items="availableEndTimeOptions"
                  item-text="label"
                  item-value="value"
                  label="End Time"
                  outlined
                  dense
                  prepend-inner-icon="mdi-clock-end"
                  :rules="[(v) => !!v || 'End time is required']"
                  :hint="
                    newTimeAway.startTime ? 'Must be after start time' : ''
                  "
                  persistent-hint
                ></v-select>
              </v-col>
            </v-row>

            <!-- Add Button -->
            <v-row class="mt-3">
              <v-col cols="12">
                <v-btn
                  color="primary"
                  @click="addTimeAway"
                  :disabled="!isTimeAwayValid"
                >
                  <v-icon left>mdi-plus</v-icon>
                  {{ editingItem ? "Update Time Away" : "Add Time Away" }}
                </v-btn>
                <v-btn
                  color="secondary"
                  text
                  @click="resetTimeAwayForm"
                  class="ml-2"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Clear Form
                </v-btn>
                <v-btn
                  v-if="editingItem"
                  color="grey"
                  text
                  @click="cancelEdit"
                  class="ml-2"
                >
                  <v-icon left>mdi-cancel</v-icon>
                  Cancel Edit
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Time Away DB recordsList -->
        <v-card outlined>
          <v-card-subtitle class="info--text">
            <v-icon left color="info">mdi-format-list-bulleted</v-icon>
            <strong>Time Away ({{ adiDBRecords.length }} entries)</strong>
          </v-card-subtitle>

          <v-card-text>
            <v-data-table
              :headers="adiDBREcordsHeaders"
              :items="filtedadiDBRecords"
              :items-per-page="10"
              class="elevation-1"
              no-data-text="No time away scheduled"
              sort-by="date"
            >
              <template v-slot:[`item.type`]="{ item }">
                <v-chip
                  small
                  :color="getTypeColor(item.type)"
                  text-color="white"
                >
                  <v-icon left small>{{ getTypeIcon(item.type) }}</v-icon>
                  {{ getTypeText(item.type) }}
                </v-chip>
              </template>

              <template v-slot:[`item.period`]="{ item }">
                <div>
                  <div class="font-weight-medium">
                    {{ item.start_date }} {{ item.start_time }} to
                    {{ item.end_date }} {{ item.end_time }}
                  </div>

                  <!--div class="text-caption text--secondary">
                    {{ formatDuration(item) }}
                  </div-->
                </div>
              </template>

              <template v-slot:[`item.reason`]="{ item }">
                <div class="text-truncate" style="max-width: 200px">
                  {{ item.reason || "No reason specified" }}
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  v-if="!item.disabled || item.status == 1"
                  icon
                  small
                  color="error"
                  @click="removeTimeAway(item)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
                <v-btn v-else icon small color="error" disabled
                  ><v-icon>mdi-delete-off</v-icon></v-btn
                >
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        
      </v-card-text>
    </v-card>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="3000"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog.show" max-width="400">
      <v-card>
        <v-card-title class="headline">
          {{ confirmDialog.title }}
        </v-card-title>
        <v-card-text>
          {{ confirmDialog.message }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="confirmDialog.show = false">
            Cancel
          </v-btn>
          <v-btn color="red darken-1" text @click="confirmDialog.action">
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from "vuex";
import {
  GET_ADI_DIARY_AWAY_TIME,
  M_OU,
  SAVE_ADI_DIARY_AWAY_TIME,
  DELETE_ADI_DIARY_AWAY_TIME,
} from "@/store/mutation-list";

export default {
  name: "TimeAway",
  props: ["adi_id"],

  data() {
    return {
      dateMenu: false,
      startDateMenu: false,
      endDateMenu: false,

      // Time options from 12am to midnight every 30 minutes
      timeOptions: this.generateTimeOptions(),

      // Time Away Data
      timeAwayTypes: [
        { text: "Hours Away", value: "hours" },
        { text: "Day Away", value: "day" },
        { text: "Days Away (Range)", value: "days" },
      ],

      newTimeAway: {
        type: "hours",
        reason: "",
        date: null,
        startTime: "",
        endTime: "",
        startDate: null,
        endDate: null,
        allDay: true,
      },

      timeAwayList: [],

      timeAwayHeaders: [
        { text: "Type", value: "type", sortable: true, width: "120px" },
        { text: "Period", value: "period", sortable: false, width: "300px" },
        { text: "Reason", value: "reason", sortable: true },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          align: "center",
          width: "100px",
        },
      ],

      snackbar: {
        show: false,
        message: "",
        color: "success",
      },

      confirmDialog: {
        show: false,
        title: "",
        message: "",
        action: null,
      },

      editingItem: null,

      adiDBREcordsHeaders: [
        { text: "Type", value: "type", sortable: true },
        { text: "Period", value: "period", sortable: true },
        /* { text: "Start", value: "start", sortable: true, width: "120px" },
        { text: "End", value: "end", sortable: true, width: "120px" },*/
        {
          text: "Duration",
          value: "duration",
          sortable: false,
        },
        { text: "Reason", value: "reason", sortable: true },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],

      adiDBRecords: [],
    };
  },

  computed: {
    isTimeAwayValid() {
      const timeAway = this.newTimeAway;

      if (timeAway.type === "hours") {
        return timeAway.date && timeAway.startTime && timeAway.endTime;
      }

      if (timeAway.type === "day") {
        if (timeAway.allDay) {
          return timeAway.date;
        } else {
          return timeAway.date && timeAway.startTime && timeAway.endTime;
        }
      }

      if (timeAway.type === "days") {
        if (timeAway.allDay) {
          return timeAway.startDate && timeAway.endDate;
        } else {
          return (
            timeAway.startDate &&
            timeAway.endDate &&
            timeAway.startTime &&
            timeAway.endTime
          );
        }
      }

      return false;
    },

    // Minimum date for date pickers (today's date)
    minDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    // Minimum end date for date ranges (must be same or after start date)
    minEndDate() {
      if (this.newTimeAway.startDate) {
        return this.newTimeAway.startDate;
      }
      return this.minDate;
    },

    // Check if selected date is today (for time validation)
    isSelectedDateToday() {
      if (!this.newTimeAway.date) return false;
      const selectedDate = new Date(this.newTimeAway.date);
      const today = new Date();

      return selectedDate.toDateString() === today.toDateString();
    },

    // Check if selected start date is today (for range validation)
    isStartDateToday() {
      if (!this.newTimeAway.startDate) return false;
      const selectedDate = new Date(this.newTimeAway.startDate);
      const today = new Date();

      return selectedDate.toDateString() === today.toDateString();
    },

    // Minimum time for today's date (current time + 1 hour buffer)
    minTimeForToday() {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      // Add 1 hour buffer and round to next 30-minute interval
      let minHour = currentHour + 1;
      let minMinute = currentMinute > 30 ? 30 : 0;

      if (minMinute === 30 && currentMinute > 30) {
        minHour += 1;
        minMinute = 0;
      }

      // Handle 24-hour overflow
      if (minHour >= 24) {
        minHour = 23;
        minMinute = 30;
      }

      return `${minHour.toString().padStart(2, "0")}:${minMinute
        .toString()
        .padStart(2, "0")}`;
    },

    // Filtered time options based on date selection
    availableStartTimeOptions() {
      if (this.isSelectedDateToday || this.isStartDateToday) {
        const minTime = this.minTimeForToday;
        return this.timeOptions.filter((option) => option.value >= minTime);
      }
      return this.timeOptions;
    },

    // Filtered end time options (must be after start time)
    availableEndTimeOptions() {
      let filteredOptions = this.timeOptions;

      // If date is today, filter by minimum time
      if (this.isSelectedDateToday || this.isStartDateToday) {
        const minTime = this.minTimeForToday;
        filteredOptions = this.timeOptions.filter(
          (option) => option.value >= minTime
        );
      }

      // If start time is selected, end time must be after start time
      if (this.newTimeAway.startTime) {
        const startTimeMinutes = this.timeToMinutes(this.newTimeAway.startTime);
        filteredOptions = filteredOptions.filter((option) => {
          const optionMinutes = this.timeToMinutes(option.value);
          return optionMinutes > startTimeMinutes;
        });
      }

      return filteredOptions;
    },

    // get filterd adiDBRecords
    filtedadiDBRecords() {
      if (this.adiDBRecords == []) {
        return [];
      }
      let tempLst = this.adiDBRecords;
      tempLst.forEach((rec) => {
        if (rec.editable == 0 || rec.status == 0) {
          rec.disabled = true;
        } else {
          rec.disabled = false;
        }
      });
      return this.adiDBRecords;
    },
  },

  methods: {
    ...mapActions({
      saveTARecord: `${M_OU}${SAVE_ADI_DIARY_AWAY_TIME}`,
      getTAData: `${M_OU}${GET_ADI_DIARY_AWAY_TIME}`,
      deleteAwayRecord: `${M_OU}${DELETE_ADI_DIARY_AWAY_TIME}`,
    }),
    // Generate time options from 12am to midnight every 30 minutes
    generateTimeOptions() {
      const options = [];

      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const timeValue = `${hour.toString().padStart(2, "0")}:${minute
            .toString()
            .padStart(2, "0")}`;
          const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
          const period = hour < 12 ? "AM" : "PM";
          const minuteStr = minute === 0 ? "00" : minute.toString();

          options.push({
            value: timeValue,
            label: `${displayHour}:${minuteStr} ${period}`,
          });
        }
      }

      return options;
    },

    // Format time for display (convert 24hr to 12hr format)
    formatTimeDisplay(time24) {
      if (!time24) return "";

      const [hour, minute] = time24.split(":").map(Number);
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const period = hour < 12 ? "AM" : "PM";
      const minuteStr = minute.toString().padStart(2, "0");

      return `${displayHour}:${minuteStr} ${period}`;
    },

    // Convert time to minutes for calculations
    timeToMinutes(time) {
      if (!time) return 0;
      const [hours, minutes] = time.split(":").map(Number);
      return hours * 60 + minutes;
    },

    // Handle type change
    onTypeChange() {
      // Store the current type
      const currentType = this.newTimeAway.type;

      // Reset form but preserve the type
      this.newTimeAway = {
        type: currentType,
        reason: "",
        date: null,
        startTime: "",
        endTime: "",
        startDate: null,
        endDate: null,
        allDay: true,
      };
    },

    // Validate that selected dates are not in the past
    validateDates() {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day

      if (
        this.newTimeAway.type === "hours" ||
        this.newTimeAway.type === "day"
      ) {
        if (this.newTimeAway.date) {
          const selectedDate = new Date(this.newTimeAway.date);
          if (selectedDate < today) {
            this.showSnackbar("Cannot select past dates", "error");
            return false;
          }
        }
      }

      if (this.newTimeAway.type === "days") {
        if (this.newTimeAway.startDate) {
          const startDate = new Date(this.newTimeAway.startDate);
          if (startDate < today) {
            this.showSnackbar("Start date cannot be in the past", "error");
            return false;
          }
        }

        if (this.newTimeAway.endDate) {
          const endDate = new Date(this.newTimeAway.endDate);
          if (endDate < today) {
            this.showSnackbar("End date cannot be in the past", "error");
            return false;
          }
        }
      }

      return true;
    },

    // Validate time selection for today's date
    validateTimes() {
      if (!this.isSelectedDateToday && !this.isStartDateToday) {
        return true; // No validation needed for future dates
      }

      const now = new Date();
      const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();

      if (this.newTimeAway.startTime) {
        const startTimeMinutes = this.timeToMinutes(this.newTimeAway.startTime);
        if (startTimeMinutes <= currentTimeMinutes) {
          this.showSnackbar("Start time must be in the future", "error");
          return false;
        }
      }

      return true;
    },

    // Time Away Methods
    addTimeAway() {
      if (!this.isTimeAwayValid || !this.adi_id) return;

      // Validate dates and times
      if (!this.validateDates() || !this.validateTimes()) {
        return;
      }

      /*const timeAway = {
        id: this.editingItem ? this.editingItem.id : Date.now(),
        type: this.newTimeAway.type,
        reason: this.newTimeAway.reason || "No reason specified",
        ...this.newTimeAway,
      };*/

      //all the validations are done. lets prepare the save data in the format we need to save
      /*
        {
        adi:
        type:
        reason:
        start_datetime:
        end_datetime:
        duration_text:
        }
      */
      let saving_data = {};
      saving_data.duration = this.formatDuration(this.newTimeAway);
      //function to get starting date time
      if (this.newTimeAway.type == "hours") {
        saving_data.start = `${this.newTimeAway.date} ${this.newTimeAway.startTime}:00`;
        saving_data.end = `${this.newTimeAway.date} ${this.newTimeAway.endTime}:00`;
      } else if (this.newTimeAway.type == "day") {
        if (this.newTimeAway.allDay) {
          saving_data.start = `${this.newTimeAway.date} 00:00:00`;
          saving_data.end = `${this.newTimeAway.date} 23:59:59`;
        } else {
          //allday false
          saving_data.start = `${this.newTimeAway.date} ${this.newTimeAway.startTime}:00`;
          saving_data.end = `${this.newTimeAway.date} ${this.newTimeAway.endTime}:00`;
        }
      } else {
        //days away range
        if (this.newTimeAway.allDay) {
          saving_data.start = `${this.newTimeAway.startDate} 00:00:00`;
          saving_data.end = `${this.newTimeAway.endDate} 23:59:59`;
        } else {
          //allDay false
          saving_data.start = `${this.newTimeAway.startDate} ${this.newTimeAway.startTime}:00`;
          saving_data.end = `${this.newTimeAway.endDate} ${this.newTimeAway.endTime}:00`;
        }
      }

      // console.log(timeAway);
      // console.log("Saving data as follows");
      // console.log(saving_data);
      this.saveTARecord({
        adi: this.adi_id,
        type: this.newTimeAway.type,
        reason: this.newTimeAway.reason,
        start_datetime: saving_data.start,
        end_datetime: saving_data.end,
        duration_text: saving_data.duration,
      }).then((res) => {
        //response from store. All complete just need to show to popup message
        //console.log(res);
        if (res == "SUCCESS") {
          this.$fire({
            title: "Success",
            text: "Instructor time away saved",
            type: "success",
            timer: 3000,
          }).then((r) => {
            console.log(r.value);
            //clear all and reloadclear()
            this.resetTimeAwayForm();
            this.getADITimeAwayRecords();
            //this.$router.go();
          });
          //need to load new data from server
        } else {
          this.$fire({
            title: "Error",
            text: "Error saving instructor time away. Please try again.",
            type: "error",
            allowOutsideClick: false,
            //timer: 3000,
          }).then((r) => {
            console.log(r.value);
          });
        }
      });

     /* if (this.editingItem) {
        // Update existing item
        const index = this.timeAwayList.findIndex(
          (t) => t.id === this.editingItem.id
        );
        if (index > -1) {
          this.timeAwayList.splice(index, 1, timeAway);
          this.showSnackbar("Time away updated successfully", "success");
        }
        this.editingItem = null;
      } else {
        // Add new item
        this.timeAwayList.push(timeAway);
        this.showSnackbar("Time away added successfully", "success");
      }*/

      this.resetTimeAwayForm();
    },
    getADITimeAwayRecords() {
      this.getTAData({ adiid: this.adi_id }).then((rep) => {
        if (rep) {
          let repData = rep;
          if (Object.hasOwn(repData, "result")) {
            //result propery means ther is an erro
            //do nothting
            this.adiDBRecords = [];
          } else {
            //there are some data lets fil the table.
            this.adiDBRecords = repData;
          }
          //console.log(rep);
        }
      });
    },

    editTimeAway(item) {
      this.editingItem = item;
      this.newTimeAway = { ...item };
      this.showSnackbar("Editing time away entry", "info");

      // Scroll to form
      this.$nextTick(() => {
        this.$el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },

    cancelEdit() {
      this.editingItem = null;
      this.resetTimeAwayForm();
      this.showSnackbar("Edit cancelled", "info");
    },

    removeTimeAway(item) {
      console.log(item);
      this.confirmDialog = {
        show: true,
        title: "Confirm Deletion",
        message: `Are you sure you want to remove this time away entry?`,
        action: () => {
          console.log("Inside action IE deleting");

          //delete happens here.
          this.deleteAwayRecord({
            recordid: item.rec_id,
          }).then((res) => {
            if (res == "SUCCESS") {
              this.$fire({
                title: "Success",
                text: "Instructor time away deleted",
                type: "success",
                timer: 3000,
              }).then((r) => {
                console.log(r.value);
                this.getADITimeAwayRecords();
                //clear all and reloadclear()
                //reload the time away
              });
              //need to load new data from server
            } else {
              this.$fire({
                title: "Error",
                text: "Error deleting instructor time away. Please try again.",
                type: "error",
                allowOutsideClick: false,
                //timer: 3000,
              }).then((r) => {
                console.log(r.value);
              });
            }
          });
          /*
          const index = this.timeAwayList.findIndex((t) => t.id === item.id);
          if (index > -1) {

           
            this.timeAwayList.splice(index, 1);
            this.showSnackbar("Time away removed", "info");
          }*/
          this.confirmDialog.show = false;
        },
      };
    },

    clearAllTimeAway() {
      if (this.timeAwayList.length === 0) {
        this.showSnackbar("No time away entries to clear", "warning");
        return;
      }

      this.confirmDialog = {
        show: true,
        title: "Clear All Time Away",
        message: `Are you sure you want to remove all ${this.timeAwayList.length} time away entries? This action cannot be undone.`,
        action: () => {
          this.timeAwayList = [];
          this.showSnackbar("All time away entries cleared", "info");
          this.confirmDialog.show = false;
        },
      };
    },

    resetTimeAwayForm() {
      this.newTimeAway = {
        type: "hours",
        reason: "",
        date: null,
        startTime: "",
        endTime: "",
        startDate: null,
        endDate: null,
        allDay: true,
      };
      this.editingItem = null;
    },

    formatPeriod(item) {
      if (item.type === "hours") {
        return `${this.formatDate(item.date)} ${this.formatTimeDisplay(
          item.startTime
        )} - ${this.formatTimeDisplay(item.endTime)}`;
      }

      if (item.type === "day") {
        if (item.allDay) {
          return `${this.formatDate(item.date)} (All Day)`;
        } else {
          return `${this.formatDate(item.date)} ${this.formatTimeDisplay(
            item.startTime
          )} - ${this.formatTimeDisplay(item.endTime)}`;
        }
      }

      if (item.type === "days") {
        const dateRange = `${this.formatDate(
          item.startDate
        )} - ${this.formatDate(item.endDate)}`;
        if (item.allDay) {
          return `${dateRange} (All Days)`;
        } else {
          return `${dateRange} ${this.formatTimeDisplay(
            item.startTime
          )} - ${this.formatTimeDisplay(item.endTime)}`;
        }
      }

      return "";
    },

    formatDuration(item) {
      if (item.type === "hours") {
        const start = this.timeToMinutes(item.startTime);
        const end = this.timeToMinutes(item.endTime);
        let totalMinutes = end - start;

        // Handle overnight periods
        if (end < start) {
          totalMinutes = 24 * 60 - start + end;
        }

        const hours = (totalMinutes / 60).toFixed(1);
        return `${hours} hours`;
      }

      if (item.type === "day") {
        if (item.allDay) {
          return "1 full day";
        } else {
          const start = this.timeToMinutes(item.startTime);
          const end = this.timeToMinutes(item.endTime);
          let totalMinutes = end - start;

          if (end < start) {
            totalMinutes = 24 * 60 - start + end;
          }

          const hours = (totalMinutes / 60).toFixed(1);
          return `${hours} hours`;
        }
      }

      if (item.type === "days") {
        const startDate = new Date(item.startDate);
        const endDate = new Date(item.endDate);
        const days =
          Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

        if (item.allDay) {
          return `${days} full days`;
        } else {
          const start = this.timeToMinutes(item.startTime);
          const end = this.timeToMinutes(item.endTime);
          let totalMinutes = end - start;

          if (end < start) {
            totalMinutes = 24 * 60 - start + end;
          }

          const hoursPerDay = (totalMinutes / 60).toFixed(1);
          const totalHours = (days * hoursPerDay).toFixed(1);
          return `${days} days (${hoursPerDay}h/day, ${totalHours}h total)`;
        }
      }

      return "";
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    getTypeColor(type) {
      const colors = {
        hours: "orange",
        day: "red",
        days: "purple",
      };
      return colors[type] || "grey";
    },

    getTypeIcon(type) {
      const icons = {
        hours: "mdi-clock-outline",
        day: "mdi-calendar-today",
        days: "mdi-calendar-range",
      };
      return icons[type] || "mdi-calendar";
    },

    getTypeText(type) {
      const texts = {
        hours: "Hours",
        day: "Day",
        days: "Days",
      };
      return texts[type] || type;
    },

    // General Methods
    saveTimeAway() {
      const timeAwayData = {
        timeAway: this.timeAwayList,
        lastUpdated: new Date().toISOString(),
      };

      this.$emit("time-away-saved", timeAwayData);
      this.showSnackbar("Time away data saved successfully!", "success");

      localStorage.setItem("timeAwaySettings", JSON.stringify(timeAwayData));
    },

    showSnackbar(message, color = "success") {
      this.snackbar = {
        show: true,
        message,
        color,
      };
    },

    loadTimeAway() {
      const saved = localStorage.getItem("timeAwaySettings");
      if (saved) {
        try {
          const data = JSON.parse(saved);
          if (data.timeAway) {
            this.timeAwayList = data.timeAway;
          }
        } catch (error) {
          console.error("Error loading time away data:", error);
        }
      }
    },
  },

  watch: {
    // Reset time fields when switching between all day and specific times
    "newTimeAway.allDay"(newVal) {
      if (newVal) {
        this.newTimeAway.startTime = "";
        this.newTimeAway.endTime = "";
      }
    },

    // Clear end time if start time changes and end time is now invalid
    "newTimeAway.startTime"() {
      if (this.newTimeAway.startTime && this.newTimeAway.endTime) {
        const startMinutes = this.timeToMinutes(this.newTimeAway.startTime);
        const endMinutes = this.timeToMinutes(this.newTimeAway.endTime);

        if (endMinutes <= startMinutes) {
          this.newTimeAway.endTime = "";
        }
      }
    },

    // Clear date-dependent times when date changes
    "newTimeAway.date"() {
      if (this.isSelectedDateToday) {
        // Clear times that might now be invalid
        const minTime = this.minTimeForToday;
        if (
          this.newTimeAway.startTime &&
          this.newTimeAway.startTime < minTime
        ) {
          this.newTimeAway.startTime = "";
          this.newTimeAway.endTime = "";
        }
      }
    },

    // Combined watcher for start date changes
    "newTimeAway.startDate"() {
      // Clear date-dependent times when start date changes
      if (this.isStartDateToday) {
        // Clear times that might now be invalid
        const minTime = this.minTimeForToday;
        if (
          this.newTimeAway.startTime &&
          this.newTimeAway.startTime < minTime
        ) {
          this.newTimeAway.startTime = "";
          this.newTimeAway.endTime = "";
        }
      }

      // Clear end date if it becomes invalid when start date changes
      if (this.newTimeAway.startDate && this.newTimeAway.endDate) {
        if (this.newTimeAway.endDate < this.newTimeAway.startDate) {
          this.newTimeAway.endDate = null;
        }
      }
    },
  },
  mounted() {
    this.loadTimeAway();
    this.getADITimeAwayRecords();
  },
};
</script>

<style scoped>
.v-card-subtitle {
  font-weight: 600;
}

.v-chip {
  margin: 2px;
}

.v-data-table {
  border-radius: 8px;
}

.text-caption {
  font-size: 0.75rem !important;
}

.font-weight-medium {
  font-weight: 500 !important;
}

/* Custom scrollbar for better UX */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Enhanced card styling */
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08) !important;
}

/* Time dropdown styling */
.v-select .v-select__selections {
  font-weight: 500;
}

/* Animation for form sections */
.v-row {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Better spacing for mobile */
@media (max-width: 600px) {
  .v-container {
    padding: 8px;
  }

  .v-card-text {
    padding: 16px !important;
  }

  .v-btn {
    margin-bottom: 8px;
  }
}

/* Enhanced button styling */
.v-btn {
  text-transform: none;
  font-weight: 500;
}

/* Table row hover effect */
.v-data-table tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

/* Form validation styling */
.v-text-field--outlined.error--text .v-input__control .v-input__slot {
  border-color: #ff5252 !important;
}

/* Enhanced chip styling */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* Loading state */
.v-btn--loading {
  pointer-events: none;
}

/* Date picker styling */
.v-date-picker {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Switch styling */
.v-input--switch .v-input__slot {
  margin-bottom: 0;
}

/* Hint text styling */
.v-messages__message {
  font-size: 0.75rem;
  color: #666;
}

/* Error state for form fields */
.error--text .v-label {
  color: #ff5252 !important;
}

/* Success state styling */
.success--text {
  color: #4caf50 !important;
}

/* Info state styling */
.info--text {
  color: #2196f3 !important;
}

/* Warning state styling */
.warning--text {
  color: #ff9800 !important;
}

/* Disabled button styling */
.v-btn--disabled {
  opacity: 0.6 !important;
}

/* Table header styling */
.v-data-table-header th {
  font-weight: 600 !important;
  color: #424242 !important;
}

/* Truncated text with tooltip */
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Form section spacing */
.v-card-text .v-row {
  margin-bottom: 8px;
}

/* Action button group */
.v-btn + .v-btn {
  margin-left: 8px;
}

/* Responsive table */
@media (max-width: 768px) {
  .v-data-table {
    font-size: 0.875rem;
  }

  .v-data-table th,
  .v-data-table td {
    padding: 0 8px !important;
  }
}

/* Enhanced focus states */
.v-text-field--outlined .v-input__slot:focus-within {
  border-color: #1976d2 !important;
  border-width: 2px !important;
}

/* Smooth transitions */
.v-input,
.v-btn,
.v-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Loading spinner customization */
.v-btn .v-progress-circular {
  margin-right: 8px;
}

/* Enhanced snackbar positioning */
.v-snack--top {
  top: 24px;
}

/* Dialog styling */
.v-dialog .v-card {
  border-radius: 8px;
}

/* Menu styling */
.v-menu__content {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Time picker specific styling */
.v-time-picker {
  border-radius: 8px;
}

/* Enhanced form layout */
.v-card--outlined {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

/* Improved accessibility */
.v-btn:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* Print styles */
@media print {
  .v-btn,
  .v-snackbar,
  .v-dialog {
    display: none !important;
  }
}
</style>
